/**
 * LANGUAGES — Panel del Promotor Digital.
 *
 * Extiende LanguageManagerBase (shared SSOT). 4 idiomas: es, en, pt, ru.
 * Consumo: import { t } from './languages.js'  →  t('compartir.titulo')
 *
 * El idioma se persiste por brand (storage-namespace) y se puede setear con
 * i18n.setLanguage(code). Por defecto cae al idioma del nodo si el panel lo
 * propaga (ver app.js), sino 'es'.
 */

import { LanguageManagerBase } from '@shared/config/games/lotowheel/i18n-base.js';

export var LANGUAGES = {
    es: {
        code: 'es',
        name: 'Español',
        errorEmail: {
            noSePudoEnviar:
                'No pudimos enviarte el correo en este momento. Intenta de nuevo en unos minutos; si sigue fallando, avisa a tu operador.',
        },
        common: {
            cargando: 'Cargando…',
            reintentar: 'Reintentar',
            sinDatos: 'Sin datos en este período',
            desde: 'Desde',
            hasta: 'Hasta',
            actualizar: 'Actualizar',
        },
        login: {
            titulo: 'Promotor Digital',
            subtitulo: 'Ingresá con tu cuenta',
            correo: 'Correo',
            password: 'Contraseña',
            entrar: 'Ingresar',
            entrando: 'Ingresando…',
            completar: 'Completá correo y contraseña',
            error: 'No se pudo ingresar',
            sesionExpiro: 'Tu sesión expiró, ingresá de nuevo.',
            sinAcceso: 'Abrí el enlace de acceso que te pasó tu operador para activar tu panel en este dispositivo.',
            enlaceRevocado: 'Tu enlace de acceso ya no es válido. Pedile a tu operador uno nuevo.',
            olvido: '¿Olvidaste tu contraseña?',
        },
        activacion: {
            tituloBienvenida: '¡Bienvenido!',
            subtituloCorreo: 'Verificá tu correo para activar tu cuenta',
            subtituloPassword: 'Creá tu contraseña para empezar',
            tuCorreo: 'Tu correo',
            enviarCodigo: 'Enviarme el código',
            enviando: 'Enviando…',
            codigoEnviado: 'Te enviamos un código a',
            codigo: 'Código de 6 dígitos',
            reenviar: 'Reenviar código',
            password: 'Tu contraseña (mínimo 8 caracteres)',
            passwordRepetir: 'Repetí la contraseña',
            crearCuenta: 'Activar mi cuenta',
            creando: 'Activando…',
            passwordCorta: 'La contraseña debe tener al menos 8 caracteres',
            passwordNoCoincide: 'Las contraseñas no coinciden',
            correoInvalido: 'Ingresá un correo válido',
            codigoRequerido: 'Ingresá el código que te llegó por correo',
            listo: '¡Cuenta activada! Ingresá con tu correo y contraseña.',
            error: 'No se pudo activar la cuenta',
            cuentaPendiente: 'Tu cuenta está lista para activar.',
            subtituloSeleccion: 'Hay más de una cuenta en esta caja — indicá tu correo para continuar',
            continuar: 'Continuar',
        },
        cambiarPass: {
            titulo: 'Cambiar contraseña',
            subtitulo: 'Tu contraseña actual y la nueva, dos veces',
            actual: 'Contraseña actual',
            nueva: 'Nueva contraseña (mínimo 8 caracteres)',
            nuevaRepetir: 'Repetí la nueva contraseña',
            cambiar: 'Cambiar contraseña',
            cambiando: 'Cambiando…',
            faltaActual: 'Ingresá tu contraseña actual',
            listo: 'Contraseña cambiada ✓',
            error: 'No se pudo cambiar la contraseña',
        },
        recuperar: {
            titulo: 'Recuperar contraseña',
            subtitulo: 'Te enviamos un código al correo registrado de tu cuenta',
            nueva: 'Tu nueva contraseña (mínimo 8 caracteres)',
            restablecer: 'Restablecer contraseña',
            restableciendo: 'Restableciendo…',
            volver: 'Volver al ingreso',
            listo: '¡Contraseña restablecida! Ingresá con la nueva.',
            error: 'No se pudo restablecer la contraseña',
        },
        cambiarCorreo: {
            titulo: 'Cambiar correo',
            subtitulo: 'Tu nuevo correo recibe un código de verificación',
            actual: 'Correo actual',
            nuevo: 'Tu nuevo correo',
            passwordActual: 'Tu contraseña actual',
            confirmar: 'Confirmar cambio',
            confirmando: 'Confirmando…',
            faltaPassword: 'Ingresá tu contraseña actual',
            listo: 'Correo actualizado ✓ Es tu nuevo usuario de ingreso.',
            error: 'No se pudo cambiar el correo',
        },
        nav: {
            compartir: 'Compartir',
            afiliados: 'Afiliados',
            reditos: 'Réditos',
            embudo: 'Embudo',
            tags: 'Tags',
            campanas: 'Campañas',
            ranking: 'Ranking',
            mas: 'Más',
            cambiarPass: 'Cambiar contraseña',
            cambiarCorreo: 'Cambiar correo',
        },
        onboarding: {
            titulo: '¡Bienvenido, Promotor!',
            intro: 'Así ganas compartiendo tu enlace:',
            paso1: 'Comparte tu enlace por WhatsApp, Facebook o Instagram.',
            paso2: 'Quienes entren por tu enlace y jueguen quedan ligados a ti.',
            paso3: 'Ganas un % de comisión por lo que generan. Lo cobras de la central.',
            empezar: 'Empezar',
        },
        bienvenida: {
            saludo: 'Acabas de entrar 🔓',
            marca: 'GOLDEN PARCE',
            frase: 'Estás dentro del círculo donde la gente comparte y gana.',
            instalarTitulo: 'Instala tu app',
            instalarSub: 'Tu acceso siempre a un toque.',
            instalarBoton: 'Instalar Golden Parce',
            instalarRecomendado: 'Recomendado',
            seguirWeb: 'Seguir en el navegador',
            // Guía iOS Safari (Compartir → Agregar a inicio)
            iosTitulo: 'Instalar en tu iPhone',
            iosPaso1: 'Toca Compartir',
            iosPaso2: 'Elige "Agregar a inicio"',
            iosPaso3: 'Listo: abre Golden Parce desde tu pantalla',
            iosOk: 'Entendido',
            // Estás en Chrome/Firefox iOS (no Safari)
            iosOtroTitulo: 'Ábrelo en Safari',
            iosOtroTexto: 'Para instalar la app en iPhone, abre este enlace en Safari.',
            // Estás dentro de WhatsApp/Instagram (in-app)
            inAppTitulo: 'Ábrelo en tu navegador',
            inAppTexto: 'Toca el menú (⋯) y elige "Abrir en Chrome" o "Abrir en el navegador" para instalar tu app.',
            inAppBoton: 'Abrir en el navegador',
            // Caja para pegar/escribir el código en la app instalada
            codigoTitulo: '¡Ya casi! Conecta tu cuenta',
            codigoTexto: 'Escribe o pega tu código de acceso (lo tienes en tu mensaje de WhatsApp).',
            codigoPlaceholder: 'Tu código de 6 dígitos',
            codigoPegar: 'Pegar',
            codigoPegarManual: 'Pega tu código aquí (mantén pulsado → Pegar).',
            iosCopiaPrimero: 'IMPORTANTE: copia tu código para pegarlo después',
            codigoCopiar: 'Copiar',
            codigoCopiado: 'Copiado',
            iosCopiaManual: 'Mantén pulsado el número y toca Copiar',
            codigoConectar: 'Conectar',
            codigoError: 'Ese código no es válido. Revísalo e intenta de nuevo.',
            codigoErrorRed: 'Sin conexión. Revisa tu internet e intenta de nuevo.',
            codigoConectando: 'Conectando…',
        },
        editor: {
            negrita: 'Negrita',
            cursiva: 'Cursiva',
            tachado: 'Tachado',
            cita: 'Cita',
            previa: 'Vista previa:',
            teclado: 'Teclado especial',
            emojis: 'Emojis',
            espacio: 'espacio',
        },
        compartir: {
            heroTitulo: 'Compartí y empezá a ganar',
            titulo: 'Tu enlace',
            descripcion: 'Compartí. Ellos juegan, vos ganás. 💸',
            waLista: 'WhatsApp: a mis contactos o grupos',
            novoNumero: 'Nuevo número',
            meusContatos: 'Mis contactos\no grupos',
            waNumPlaceholder: 'Número nuevo (con código país)',
            meta: 'Compartir en Facebook / Instagram',
            redes: 'Redes',
            copiar: 'Copiar enlace',
            qr: 'Mostrar QR',
            cerrar: 'Cerrar',
            qrSub: 'Escaneá para entrar 📲',
            incluirMensaje: 'Incluir mensaje',
            incluirImagen: 'Incluir imagen',
            emBreve: 'Pronto',
            galeria: 'Mensajes',
            galeriaBtn: 'Modelos',
            mensajePlaceholder: 'Escribí tu mensaje…',
            guardarActual: 'Guardar este mensaje',
            misMensajes: 'Mis mensajes',
            misMensajesVacio: 'Aún no guardaste mensajes. Escribí uno y tocá "Guardar".',
            sugeridos: 'Sugeridos',
            copiado: '¡Enlace copiado!',
            mensajeLabel: 'Mensaje (editable)',
            sinPasarela: 'Tu enlace todavía no acepta recargas — pedí a tu operador que active la pasarela.',
            mensajeDefault: '¡Jugá conmigo! 🎰 Registrate acá:',
            creatividadesLabel: 'Mensajes listos (tocá uno):',
            creatividades: [
                '➤ *El premio está ahí. Faltás vos.*\nR$ 5 pueden volverse *R$ 5.000* en un giro ⚡\n👉 *Entrá ya y girá:*',
                '✦ *Multiplicá tu apuesta hasta x500* en segundos\n_Los mismos R$ que gastás en un café_, otra historia 🔥\n👉 *Transformá ahora:*',
                '🎯 *R$ 5 → R$ 5.000* en un giro\nLa mecánica del salto es _real, no suerte ciega_\n👉 *Pegá tu multiplicador:*',
                '✦ Tu grupo ya está jugando\n✦ Vos sos el único que falta\n_No dejes que te lo cuenten después._ 👉 *Sumate:*',
                '⚡ *Girá y ganá* en segundos\n✦ Apuesta chica ▸ premio grande ▸ un solo giro\n👉 *Multiplicá ya:*',
                '🎰 ¡Jugá conmigo! Registrate y empezá a ganar:',
            ],
        },
        afiliados: {
            titulo: 'Mis afiliados',
            jugador: 'Jugador',
            volumen: 'Jugado',
            ggr: 'GGR',
            totalAfiliados: 'Afiliados',
            estado: 'Estado',
            activo: 'Activo',
            dormido: 'Dormido',
            sinJugar: 'Sin jugar',
            recargado: 'Recargó',
            filtroTodos: 'Todos',
            filtroPorCobrar: 'Por cobrar',
            filtroFieis: 'Fieles',
            filtroInvestiu: 'Invirtió',
            filtroSoloBono: 'Solo bono',
            copiarNumeros: 'Copiar números',
            numerosCopiados: 'Números copiados',
            copiarMensaje: 'Copiar mensaje',
            mensajeCopiado: 'Mensaje copiado',
            guiaPorCobrarQuienes: 'Ganaron premio y no lo retiraron. Muchos no saben que pueden.',
            guiaPorCobrarTono: 'Tono: avisar, no vender.',
            guiaPorCobrarMensaje: '¡Hola! Tenés premio esperando en tu cuenta. Solo entrá y retiralo, lleva un minuto.',
            guiaFielQuienes: 'Recargaron y ya cobraron: comprobaron que la casa paga.',
            guiaFielTono: 'Tono: cercano, como a un cliente conocido.',
            guiaFielMensaje: '¡Hola! Hay sorteo hoy. Te aviso primero a vos.',
            guiaInvirtioQuienes: 'Pusieron dinero propio y todavía no cobraron.',
            guiaInvirtioTono: 'Tono: acompañar, estar disponible.',
            guiaInvirtioMensaje: '¡Hola! ¿Cómo va el juego? Cualquier cosa escribime y te ayudo.',
            guiaSoloBonoQuienes: 'Usaron el bono y no volvieron.',
            guiaSoloBonoTono: 'Tono: invitar sin insistir.',
            guiaSoloBonoMensaje: '¡Hola! Te aviso que hubo gente ganando bien esta semana.',
            repasado: 'Repasado',
            repasoHoy: 'hoy',
            repasoVolvio: 'volvió',
            confirmarEnvio: '¿Le escribiste a',
            confirmarSi: 'Sí',
            confirmarNo: 'Todavía no',
            prioridadHoy: 'Para contactar hoy',
            marcadorRepasados: 'Repasados',
            marcadorVolvieron: 'volvieron',
        },
        ranking: {
            titulo: 'Ranking',
            descripcion: 'Tu posición entre los promotores de tu operador. ¡Subí captando más!',
            posicion: 'Tu posición',
            de: 'de',
            afiliados: 'afiliados',
            tu: 'Vos',
            sinDatos: 'Todavía no hay ranking en este período.',
            trofeo: { general: 'General', aquisicao: 'Adquisición', valor: 'Valor', qualidade: 'Calidad', peixe: 'Pez gordo', retencao: 'Retención', conversao: 'Conversión' },
            verbo: { general: 'es el campeón general', aquisicao: 'reina en captación', valor: 'domina el valor', qualidade: 'lidera en calidad', peixe: 'pesca los gordos', retencao: 'retiene a la gente', conversao: 'conquista la conversión' },
            vocereina: 'REINÁS acá',
            vocedomina: 'DOMINÁS',
            nopodio: '¡En el podio!',
            voce: 'Vos',
            destronar: '¡falta poco para destronar al #{n}!',
        },
        campanas: {
            titulo: 'Mis Campañas',
            descripcion: 'Mirá qué anuncio convierte mejor para invertir donde rinde.',
            campana: 'Campaña',
            fuente: 'Fuente',
            captados: 'Captados',
            recargaron: 'Recargaron',
            jugaron: 'Jugaron',
            sinDatos: 'Todavía no hay datos de campañas en este período.',
        },
        tags: {
            titulo: 'Mis Tags',
            descripcion: 'Pegá tu píxel de Meta o GTM para medir y optimizar tus anuncios. Es opcional.',
            metaPixel: 'Píxel de Meta (Facebook/Instagram)',
            metaPlaceholder: 'Ej: 1234567890123456',
            gtm: 'Google Tag Manager',
            gtmPlaceholder: 'Ej: GTM-XXXXXXX',
            guardar: 'Guardar',
            guardado: '¡Guardado!',
            invalidoMeta: 'Píxel de Meta inválido (solo números)',
            invalidoGtm: 'GTM inválido (GTM-XXXXXXX)',
            ayuda: 'Si no ponés ninguno, se usa el de tu operador.',
        },
        embudo: {
            titulo: 'Embudo',
            captados: 'Captados',
            registrados: 'Registrados',
            conSesion: 'Entraron',
            conRecarga: 'Recargaron',
            conApuesta: 'Jugaron',
        },
        reditos: {
            titulo: 'Mis réditos',
            produccion: 'Producción',
            jugado: 'Jugado',
            pagado: 'Pagado',
            ggr: 'GGR',
            comision: 'Tu comisión',
            delPeriodo: 'del período',
            aReceber: 'A cobrar',
            jaRecebido: 'Ya cobrado',
            estaSemana: 'Esta semana',
            tendSube: '🔥 ¡Vas subiendo! Compartí más y seguí creciendo.',
            tendBaja: '💪 Bajaste esta semana. ¡Compartí tu link y remontá!',
            tendNeutro: '🚀 Compartí tu link y hacé crecer tu semana.',
            tuEmbudo: 'Tu embudo',
            captados: 'Captaste',
            registrados: 'Registrados',
            jugando: 'Jugando',
            base: 'Base',
            porcentaje: 'Porcentaje',
            reditoEstimado: 'Rédito estimado',
            pendiente: 'Pendiente de cobro',
            proximoPago: 'Tu próximo pago',
            preview: 'Estimación informativa. El monto final lo confirma la central al liquidar.',
        },
        notif: {
            titulo: 'No te pierdas cuando cobrás',
            descripcion: 'Activá los avisos y enterate al instante de cada pago.',
            boton: 'Activar avisos',
            activando: 'Activando…',
            listo: '¡Listo! Te avisaremos.',
            error: 'No se pudo activar. Probá de nuevo.',
            tituloIOS: 'Sumá la app a tu inicio',
            guiaIOS: 'Tocá Compartir → "Agregar a inicio". Después activá los avisos desde acá.',
            tituloBloqueado: 'Avisos bloqueados',
            guiaBloqueado: 'Activá las notificaciones para este sitio en los ajustes de tu navegador.',
        },
        canales: {
            titulo: 'Por dónde te entran',
            descripcion: 'De qué vía llegó cada jugador que captaste. Mirá qué canal te rinde más.',
            vacio: 'Todavía no hay datos. Compartí tu link y volvé a mirar.',
            juegan: 'juegan',
            recargan: 'recargan',
            waLista: 'WhatsApp contactos',
            waNum: 'WhatsApp número',
            redes: 'Redes',
            telegram: 'Telegram',
            qr: 'QR',
            copy: 'Link copiado',
            directo: 'Directo',
        },
        misPagos: {
            titulo: 'Mis pagos',
            vacio: 'Todavía no recibiste pagos. Cuando cobres, aparecen acá.',
            error: 'No se pudieron cargar tus pagos.',
        },
        fechas: {
            hoy: 'Hoy',
            semana: '7 días',
            mes: '30 días',
        },
        instalar: {
            boton: 'Instalar app',
            iosTitulo: 'Agregá la app a tu inicio',
            iosPaso: 'Tocá Compartir y luego “Agregar a inicio”.',
            iosPaso1: 'Tocá el botón Compartir',
            iosPaso2: 'Elegí “Agregar a inicio”',
            iosPaso3: '¡Listo! Ya la tenés en tu pantalla',
            iosOk: 'Entendido',
        },
    },

    en: {
        code: 'en',
        name: 'English',
        errorEmail: {
            noSePudoEnviar:
                "We couldn't send your email right now. Please try again in a few minutes; if it keeps failing, contact your operator.",
        },
        common: {
            cargando: 'Loading…',
            reintentar: 'Retry',
            sinDatos: 'No data in this period',
            desde: 'From',
            hasta: 'To',
            actualizar: 'Refresh',
        },
        login: {
            titulo: 'Digital Promoter',
            subtitulo: 'Sign in to your account',
            correo: 'Email',
            password: 'Password',
            entrar: 'Sign in',
            entrando: 'Signing in…',
            completar: 'Enter email and password',
            error: 'Could not sign in',
            sesionExpiro: 'Your session expired, please sign in again.',
            sinAcceso: 'Open the access link your operator sent you to activate your panel on this device.',
            enlaceRevocado: 'Your access link is no longer valid. Ask your operator for a new one.',
            olvido: 'Forgot your password?',
        },
        activacion: {
            tituloBienvenida: 'Welcome!',
            subtituloCorreo: 'Verify your email to activate your account',
            subtituloPassword: 'Create your password to get started',
            tuCorreo: 'Your email',
            enviarCodigo: 'Send me the code',
            enviando: 'Sending…',
            codigoEnviado: 'We sent a code to',
            codigo: '6-digit code',
            reenviar: 'Resend code',
            password: 'Your password (8 characters minimum)',
            passwordRepetir: 'Repeat the password',
            crearCuenta: 'Activate my account',
            creando: 'Activating…',
            passwordCorta: 'Password must be at least 8 characters',
            passwordNoCoincide: 'Passwords do not match',
            correoInvalido: 'Enter a valid email',
            codigoRequerido: 'Enter the code we emailed you',
            listo: 'Account activated! Sign in with your email and password.',
            error: 'Could not activate the account',
            cuentaPendiente: 'Your account is ready to activate.',
            subtituloSeleccion: 'There is more than one account on this till — enter your email to continue',
            continuar: 'Continue',
        },
        cambiarPass: {
            titulo: 'Change password',
            subtitulo: 'Your current password and the new one, twice',
            actual: 'Current password',
            nueva: 'New password (8 characters minimum)',
            nuevaRepetir: 'Repeat the new password',
            cambiar: 'Change password',
            cambiando: 'Changing…',
            faltaActual: 'Enter your current password',
            listo: 'Password changed ✓',
            error: 'Could not change the password',
        },
        recuperar: {
            titulo: 'Recover password',
            subtitulo: 'We send a code to the email registered on your account',
            nueva: 'Your new password (8 characters minimum)',
            restablecer: 'Reset password',
            restableciendo: 'Resetting…',
            volver: 'Back to sign in',
            listo: 'Password reset! Sign in with the new one.',
            error: 'Could not reset the password',
        },
        cambiarCorreo: {
            titulo: 'Change email',
            subtitulo: 'Your new email receives a verification code',
            actual: 'Current email',
            nuevo: 'Your new email',
            passwordActual: 'Your current password',
            confirmar: 'Confirm change',
            confirmando: 'Confirming…',
            faltaPassword: 'Enter your current password',
            listo: 'Email updated ✓ It is your new sign-in user.',
            error: 'Could not change the email',
        },
        nav: {
            compartir: 'Share',
            afiliados: 'Players',
            reditos: 'Earnings',
            embudo: 'Funnel',
            tags: 'Tags',
            campanas: 'Campaigns',
            ranking: 'Ranking',
            mas: 'More',
            cambiarPass: 'Change password',
            cambiarCorreo: 'Change email',
        },
        onboarding: {
            titulo: 'Welcome, Promoter!',
            intro: 'This is how you earn by sharing your link:',
            paso1: 'Share your link via WhatsApp, Facebook or Instagram.',
            paso2: 'Anyone who joins through your link and plays is tied to you.',
            paso3: 'You earn a % commission on what they generate. Headquarters pays you.',
            empezar: 'Get started',
        },
        bienvenida: {
            saludo: "You're in 🔓",
            marca: 'GOLDEN PARCE',
            frase: 'You are inside the circle where people share and win.',
            instalarTitulo: 'Install your app',
            instalarSub: 'Your access always one tap away.',
            instalarBoton: 'Install Golden Parce',
            instalarRecomendado: 'Recommended',
            seguirWeb: 'Continue in the browser',
            iosTitulo: 'Install on your iPhone',
            iosPaso1: 'Tap Share',
            iosPaso2: 'Choose "Add to Home Screen"',
            iosPaso3: 'Done: open Golden Parce from your home screen',
            iosOk: 'Got it',
            iosOtroTitulo: 'Open it in Safari',
            iosOtroTexto: 'To install the app on iPhone, open this link in Safari.',
            inAppTitulo: 'Open it in your browser',
            inAppTexto: 'Tap the menu (⋯) and choose "Open in Chrome" or "Open in browser" to install your app.',
            inAppBoton: 'Open in browser',
            codigoTitulo: 'Almost there! Connect your account',
            codigoTexto: 'Type or paste your access code (you have it in your WhatsApp message).',
            codigoPlaceholder: 'Your 6-digit code',
            codigoPegar: 'Paste',
            codigoPegarManual: 'Paste your code here (long-press → Paste).',
            iosCopiaPrimero: 'IMPORTANT: copy your code to paste it later',
            codigoCopiar: 'Copy',
            codigoCopiado: 'Copied',
            iosCopiaManual: 'Long-press the number and tap Copy',
            codigoConectar: 'Connect',
            codigoError: "That code isn't valid. Check it and try again.",
            codigoErrorRed: 'No connection. Check your internet and try again.',
            codigoConectando: 'Connecting…',
        },
        editor: {
            negrita: 'Bold',
            cursiva: 'Italic',
            tachado: 'Strikethrough',
            cita: 'Quote',
            previa: 'Preview:',
            teclado: 'Special keyboard',
            emojis: 'Emojis',
            espacio: 'space',
        },
        compartir: {
            heroTitulo: 'Share and start earning',
            titulo: 'Your link',
            descripcion: 'Share it. They play, you earn. 💸',
            waLista: 'WhatsApp: to my contacts or groups',
            novoNumero: 'New number',
            meusContatos: 'My contacts\nor groups',
            waNumPlaceholder: 'New number (with country code)',
            meta: 'Share on Facebook / Instagram',
            redes: 'Social',
            copiar: 'Copy link',
            qr: 'Show QR',
            cerrar: 'Close',
            qrSub: 'Scan to join 📲',
            incluirMensaje: 'Include message',
            incluirImagen: 'Include image',
            emBreve: 'Soon',
            galeria: 'Messages',
            galeriaBtn: 'Templates',
            mensajePlaceholder: 'Write your message…',
            guardarActual: 'Save this message',
            misMensajes: 'My messages',
            misMensajesVacio: 'No saved messages yet. Write one and tap "Save".',
            sugeridos: 'Suggested',
            copiado: 'Link copied!',
            mensajeLabel: 'Message (editable)',
            sinPasarela: 'Your link does not accept top-ups yet — ask your operator to enable the gateway.',
            mensajeDefault: 'Play with me! 🎰 Sign up here:',
            creatividadesLabel: 'Ready messages (tap one):',
            creatividades: [
                "➤ *The prize is right there. You're missing.*\nR$ 5 can turn into *R$ 5,000* in one spin ⚡\n👉 *Jump in and spin:*",
                '✦ *Multiply your bet up to x500* in seconds\n_The same change you spend on coffee_, different story 🔥\n👉 *Transform it now:*',
                '🎯 *R$ 5 → R$ 5,000* in one spin\nThe jump mechanic is _real, not blind luck_\n👉 *Grab your multiplier:*',
                "✦ Your crew is already playing\n✦ You're the only one missing\n_Don't let them tell you later._ 👉 *Join in:*",
                '⚡ *Spin and win* in seconds\n✦ Small bet ▸ big prize ▸ one spin\n👉 *Multiply now:*',
                '🎰 Play with me! Sign up and start winning:',
            ],
        },
        afiliados: {
            titulo: 'My players',
            jugador: 'Player',
            volumen: 'Wagered',
            ggr: 'GGR',
            totalAfiliados: 'Players',
            estado: 'Status',
            activo: 'Active',
            dormido: 'Dormant',
            sinJugar: 'No play yet',
            recargado: 'Deposited',
            filtroTodos: 'All',
            filtroPorCobrar: 'To collect',
            filtroFieis: 'Loyal',
            filtroInvestiu: 'Deposited',
            filtroSoloBono: 'Bonus only',
            copiarNumeros: 'Copy numbers',
            numerosCopiados: 'Numbers copied',
            copiarMensaje: 'Copy message',
            mensajeCopiado: 'Message copied',
            guiaPorCobrarQuienes: 'They won a prize and never withdrew it. Most do not know they can.',
            guiaPorCobrarTono: 'Tone: inform, do not sell.',
            guiaPorCobrarMensaje: 'Hi! You have a prize waiting in your account. Just log in and withdraw it.',
            guiaFielQuienes: 'They deposited and already withdrew: they know the house pays.',
            guiaFielTono: 'Tone: friendly, like a known customer.',
            guiaFielMensaje: 'Hi! There is a draw today. Telling you first.',
            guiaInvirtioQuienes: 'They put in their own money and have not withdrawn yet.',
            guiaInvirtioTono: 'Tone: supportive, be available.',
            guiaInvirtioMensaje: 'Hi! How is the game going? Message me if you need anything.',
            guiaSoloBonoQuienes: 'They used the bonus and did not come back.',
            guiaSoloBonoTono: 'Tone: invite without pushing.',
            guiaSoloBonoMensaje: 'Hi! Just letting you know people won well this week.',
            repasado: 'Contacted',
            repasoHoy: 'today',
            repasoVolvio: 'came back',
            confirmarEnvio: 'Did you message',
            confirmarSi: 'Yes',
            confirmarNo: 'Not yet',
            prioridadHoy: 'To contact today',
            marcadorRepasados: 'Contacted',
            marcadorVolvieron: 'came back',
        },
        ranking: {
            titulo: 'Ranking',
            descripcion: 'Your position among your operator\u2019s promoters. Climb by capturing more!',
            posicion: 'Your position',
            de: 'of',
            afiliados: 'affiliates',
            tu: 'You',
            sinDatos: 'No ranking in this period yet.',
            trofeo: { general: 'Overall', aquisicao: 'Acquisition', valor: 'Value', qualidade: 'Quality', peixe: 'Big fish', retencao: 'Retention', conversao: 'Conversion' },
            verbo: { general: 'is the overall champion', aquisicao: 'leads acquisition', valor: 'dominates value', qualidade: 'leads quality', peixe: 'lands the big fish', retencao: 'keeps players active', conversao: 'wins conversion' },
            vocereina: 'You RULE here',
            vocedomina: 'You DOMINATE',
            nopodio: 'On the podium!',
            voce: 'You',
            destronar: 'almost dethroning #{n}!',
        },
        campanas: {
            titulo: 'My Campaigns',
            descripcion: 'See which ad converts best to invest where it pays off.',
            campana: 'Campaign',
            fuente: 'Source',
            captados: 'Captured',
            recargaron: 'Topped up',
            jugaron: 'Played',
            sinDatos: 'No campaign data in this period yet.',
        },
        tags: {
            titulo: 'My Tags',
            descripcion: 'Paste your Meta Pixel or GTM to measure and optimize your ads. Optional.',
            metaPixel: 'Meta Pixel (Facebook/Instagram)',
            metaPlaceholder: 'e.g. 1234567890123456',
            gtm: 'Google Tag Manager',
            gtmPlaceholder: 'e.g. GTM-XXXXXXX',
            guardar: 'Save',
            guardado: 'Saved!',
            invalidoMeta: 'Invalid Meta Pixel (numbers only)',
            invalidoGtm: 'Invalid GTM (GTM-XXXXXXX)',
            ayuda: 'If you leave them empty, your operator\u2019s are used.',
        },
        embudo: {
            titulo: 'Funnel',
            captados: 'Reached',
            registrados: 'Registered',
            conSesion: 'Entered',
            conRecarga: 'Topped up',
            conApuesta: 'Played',
        },
        reditos: {
            titulo: 'My earnings',
            produccion: 'Production',
            jugado: 'Wagered',
            pagado: 'Paid out',
            ggr: 'GGR',
            comision: 'Your commission',
            delPeriodo: 'this period',
            aReceber: 'To collect',
            jaRecebido: 'Collected',
            estaSemana: 'This week',
            tendSube: '🔥 You are growing! Share more and keep it up.',
            tendBaja: '💪 Down this week. Share your link and bounce back!',
            tendNeutro: '🚀 Share your link and grow your week.',
            tuEmbudo: 'Your funnel',
            captados: 'Brought in',
            registrados: 'Signed up',
            jugando: 'Playing',
            base: 'Base',
            porcentaje: 'Percentage',
            reditoEstimado: 'Estimated earnings',
            pendiente: 'Pending payout',
            proximoPago: 'Your next payout',
            preview: 'Informative estimate. The final amount is confirmed by headquarters at settlement.',
        },
        notif: {
            titulo: "Don't miss your payouts",
            descripcion: 'Turn on alerts and know the instant you get paid.',
            boton: 'Enable alerts',
            activando: 'Enabling…',
            listo: "Done! We'll let you know.",
            error: "Couldn't enable. Try again.",
            tituloIOS: 'Add the app to your home screen',
            guiaIOS: 'Tap Share → "Add to Home Screen". Then enable alerts here.',
            tituloBloqueado: 'Alerts blocked',
            guiaBloqueado: 'Enable notifications for this site in your browser settings.',
        },
        canales: {
            titulo: 'Where they come from',
            descripcion: 'Which channel each player you brought in came through. See what works best.',
            vacio: 'No data yet. Share your link and check back.',
            juegan: 'play',
            recargan: 'top up',
            waLista: 'WhatsApp contacts',
            waNum: 'WhatsApp number',
            redes: 'Social',
            telegram: 'Telegram',
            qr: 'QR',
            copy: 'Copied link',
            directo: 'Direct',
        },
        misPagos: {
            titulo: 'My payouts',
            vacio: "No payouts yet. When you get paid, they'll show here.",
            error: "Couldn't load your payouts.",
        },
        fechas: {
            hoy: 'Today',
            semana: '7 days',
            mes: '30 days',
        },
        instalar: {
            boton: 'Install app',
            iosTitulo: 'Add the app to your home screen',
            iosPaso: 'Tap Share, then “Add to Home Screen”.',
            iosPaso1: 'Tap the Share button',
            iosPaso2: 'Choose “Add to Home Screen”',
            iosPaso3: 'Done! It is on your screen now',
            iosOk: 'Got it',
        },
    },

    pt: {
        code: 'pt',
        name: 'Português',
        errorEmail: {
            noSePudoEnviar:
                'Não foi possível enviar o e-mail neste momento. Tente novamente em alguns minutos; se continuar falhando, avise seu operador.',
        },
        common: {
            cargando: 'Carregando…',
            reintentar: 'Tentar de novo',
            sinDatos: 'Sem dados neste período',
            desde: 'De',
            hasta: 'Até',
            actualizar: 'Atualizar',
        },
        login: {
            titulo: 'Promotor Digital',
            subtitulo: 'Entre com sua conta',
            correo: 'E-mail',
            password: 'Senha',
            entrar: 'Entrar',
            entrando: 'Entrando…',
            completar: 'Preencha e-mail e senha',
            error: 'Não foi possível entrar',
            sesionExpiro: 'Sua sessão expirou, entre novamente.',
            sinAcceso: 'Abra o link de acesso que seu operador enviou para ativar seu painel neste dispositivo.',
            enlaceRevocado: 'Seu link de acesso não é mais válido. Peça um novo ao seu operador.',
            olvido: 'Esqueceu sua senha?',
        },
        activacion: {
            tituloBienvenida: 'Bem-vindo!',
            subtituloCorreo: 'Verifique seu e-mail para ativar sua conta',
            subtituloPassword: 'Crie sua senha para começar',
            tuCorreo: 'Seu e-mail',
            enviarCodigo: 'Enviar o código',
            enviando: 'Enviando…',
            codigoEnviado: 'Enviamos um código para',
            codigo: 'Código de 6 dígitos',
            reenviar: 'Reenviar código',
            password: 'Sua senha (mínimo 8 caracteres)',
            passwordRepetir: 'Repita a senha',
            crearCuenta: 'Ativar minha conta',
            creando: 'Ativando…',
            passwordCorta: 'A senha deve ter pelo menos 8 caracteres',
            passwordNoCoincide: 'As senhas não coincidem',
            correoInvalido: 'Digite um e-mail válido',
            codigoRequerido: 'Digite o código que enviamos por e-mail',
            listo: 'Conta ativada! Entre com seu e-mail e senha.',
            error: 'Não foi possível ativar a conta',
            cuentaPendiente: 'Sua conta está pronta para ativar.',
            subtituloSeleccion: 'Há mais de uma conta neste caixa — informe seu e-mail para continuar',
            continuar: 'Continuar',
        },
        cambiarPass: {
            titulo: 'Alterar senha',
            subtitulo: 'Sua senha atual e a nova, duas vezes',
            actual: 'Senha atual',
            nueva: 'Nova senha (mínimo 8 caracteres)',
            nuevaRepetir: 'Repita a nova senha',
            cambiar: 'Alterar senha',
            cambiando: 'Alterando…',
            faltaActual: 'Digite sua senha atual',
            listo: 'Senha alterada ✓',
            error: 'Não foi possível alterar a senha',
        },
        recuperar: {
            titulo: 'Recuperar senha',
            subtitulo: 'Enviamos um código ao e-mail cadastrado da sua conta',
            nueva: 'Sua nova senha (mínimo 8 caracteres)',
            restablecer: 'Redefinir senha',
            restableciendo: 'Redefinindo…',
            volver: 'Voltar ao login',
            listo: 'Senha redefinida! Entre com a nova.',
            error: 'Não foi possível redefinir a senha',
        },
        cambiarCorreo: {
            titulo: 'Alterar e-mail',
            subtitulo: 'Seu novo e-mail recebe um código de verificação',
            actual: 'E-mail atual',
            nuevo: 'Seu novo e-mail',
            passwordActual: 'Sua senha atual',
            confirmar: 'Confirmar alteração',
            confirmando: 'Confirmando…',
            faltaPassword: 'Digite sua senha atual',
            listo: 'E-mail atualizado ✓ É seu novo usuário de login.',
            error: 'Não foi possível alterar o e-mail',
        },
        nav: {
            compartir: 'Compartilhar',
            afiliados: 'Jogadores',
            reditos: 'Ganhos',
            embudo: 'Funil',
            tags: 'Tags',
            campanas: 'Campanhas',
            ranking: 'Ranking',
            mas: 'Mais',
            cambiarPass: 'Alterar senha',
            cambiarCorreo: 'Alterar e-mail',
        },
        onboarding: {
            titulo: 'Bem-vindo, Promotor!',
            intro: 'É assim que você ganha compartilhando seu link:',
            paso1: 'Compartilhe seu link pelo WhatsApp, Facebook ou Instagram.',
            paso2: 'Quem entrar pelo seu link e jogar fica vinculado a você.',
            paso3: 'Você ganha uma % de comissão sobre o que eles geram. A central te paga.',
            empezar: 'Começar',
        },
        bienvenida: {
            saludo: 'Você acabou de entrar 🔓',
            marca: 'GOLDEN PARCE',
            frase: 'Você está dentro do círculo onde a galera compartilha e ganha.',
            instalarTitulo: 'Instale seu app',
            instalarSub: 'Seu acesso sempre a um toque.',
            instalarBoton: 'Instalar Golden Parce',
            instalarRecomendado: 'Recomendado',
            seguirWeb: 'Continuar no navegador',
            iosTitulo: 'Instalar no seu iPhone',
            iosPaso1: 'Toque em Compartilhar',
            iosPaso2: 'Escolha "Adicionar à Tela de Início"',
            iosPaso3: 'Pronto: abra o Golden Parce pela sua tela',
            iosOk: 'Entendi',
            iosOtroTitulo: 'Abra no Safari',
            iosOtroTexto: 'Para instalar o app no iPhone, abra este link no Safari.',
            inAppTitulo: 'Abra no seu navegador',
            inAppTexto: 'Toque no menu (⋯) e escolha "Abrir no Chrome" ou "Abrir no navegador" para instalar seu app.',
            inAppBoton: 'Abrir no navegador',
            codigoTitulo: 'Quase lá! Conecte sua conta',
            codigoTexto: 'Digite ou cole seu código de acesso (está na sua mensagem do WhatsApp).',
            codigoPlaceholder: 'Seu código de 6 dígitos',
            codigoPegar: 'Colar',
            codigoPegarManual: 'Cola teu código aqui (segura pressionado → Colar).',
            iosCopiaPrimero: 'IMPORTANTE: copia teu código para colar depois',
            codigoCopiar: 'Copiar',
            codigoCopiado: 'Copiado',
            iosCopiaManual: 'Segura o número e toque em Copiar',
            codigoConectar: 'Conectar',
            codigoError: 'Esse código não é válido. Confira e tente de novo.',
            codigoErrorRed: 'Sem conexão. Verifique sua internet e tente de novo.',
            codigoConectando: 'Conectando…',
        },
        editor: {
            negrita: 'Negrito',
            cursiva: 'Itálico',
            tachado: 'Tachado',
            cita: 'Citação',
            previa: 'Prévia:',
            teclado: 'Teclado especial',
            emojis: 'Emojis',
            espacio: 'espaço',
        },
        compartir: {
            heroTitulo: 'Compartilhe e comece a ganhar',
            titulo: 'Seu link',
            descripcion: 'Compartilhe. Eles jogam, você ganha. 💸',
            waLista: 'WhatsApp: meus contatos ou grupos',
            novoNumero: 'Novo número',
            meusContatos: 'Meus contatos\nou grupos',
            waNumPlaceholder: 'Número novo (com código do país)',
            meta: 'Compartilhar no Facebook / Instagram',
            redes: 'Redes',
            copiar: 'Copiar link',
            qr: 'Mostrar QR',
            cerrar: 'Fechar',
            qrSub: 'Escaneie para entrar 📲',
            incluirMensaje: 'Incluir mensagem',
            incluirImagen: 'Incluir imagem',
            emBreve: 'Em breve',
            galeria: 'Mensagens',
            galeriaBtn: 'Modelos',
            mensajePlaceholder: 'Escreva sua mensagem…',
            guardarActual: 'Salvar esta mensagem',
            misMensajes: 'Minhas mensagens',
            misMensajesVacio: 'Você ainda não salvou mensagens. Escreva uma e toque em "Salvar".',
            sugeridos: 'Sugeridos',
            copiado: 'Link copiado!',
            mensajeLabel: 'Mensagem (editável)',
            sinPasarela: 'Seu link ainda não aceita recargas — peça ao seu operador para ativar o gateway.',
            mensajeDefault: 'Jogue comigo! 🎰 Cadastre-se aqui:',
            creatividadesLabel: 'Mensagens prontas (toque uma):',
            creatividades: [
                '➤ *O prêmio tá aí. Falta você.*\nR$ 5 podem virar *R$ 5.000* num giro ⚡\n👉 *Entra agora e gira:*',
                '✦ *Multiplica sua aposta até x500* em segundos\n_Os mesmos R$ que você gasta num café_, outra história 🔥\n👉 *Transforma agora:*',
                '🎯 *R$ 5 → R$ 5.000* num giro\nA mecânica do salto é _real, não sorte cega_\n👉 *Pega seu multiplicador:*',
                '✦ Sua galera já tá jogando\n✦ Você é o único que falta\n_Não deixa eles te contarem depois._ 👉 *Cola junto:*',
                '⚡ *Gira e ganha* em segundos\n✦ Aposta pequena ▸ prêmio grande ▸ um giro só\n👉 *Multiplica agora:*',
                '🎰 Jogue comigo! Cadastre-se e comece a ganhar:',
            ],
        },
        afiliados: {
            titulo: 'Meus jogadores',
            jugador: 'Jogador',
            volumen: 'Apostado',
            ggr: 'GGR',
            totalAfiliados: 'Jogadores',
            estado: 'Status',
            activo: 'Ativo',
            dormido: 'Inativo',
            sinJugar: 'Sem jogar',
            recargado: 'Recarregou',
            filtroTodos: 'Todos',
            filtroPorCobrar: 'A receber',
            filtroFieis: 'Fiéis',
            filtroInvestiu: 'Investiu',
            filtroSoloBono: 'Só bônus',
            copiarNumeros: 'Copiar números',
            numerosCopiados: 'Números copiados',
            copiarMensaje: 'Copiar mensagem',
            mensajeCopiado: 'Mensagem copiada',
            guiaPorCobrarQuienes: 'Ganharam prêmio e não sacaram. Muitos não sabem que podem.',
            guiaPorCobrarTono: 'Tom: avisar, não vender.',
            guiaPorCobrarMensaje: 'Oi! Você tem prêmio esperando na sua conta. É só entrar e sacar, leva um minuto.',
            guiaFielQuienes: 'Recarregaram e já sacaram: comprovaram que a casa paga.',
            guiaFielTono: 'Tom: próximo, como a um cliente conhecido.',
            guiaFielMensaje: 'E aí! Tem sorteio hoje. Tô avisando você primeiro.',
            guiaInvirtioQuienes: 'Colocaram dinheiro próprio e ainda não sacaram.',
            guiaInvirtioTono: 'Tom: acompanhar, estar disponível.',
            guiaInvirtioMensaje: 'Oi! Como tá indo o jogo? Qualquer coisa me chama que eu te ajudo.',
            guiaSoloBonoQuienes: 'Usaram o bônus e não voltaram.',
            guiaSoloBonoTono: 'Tom: convidar sem insistir.',
            guiaSoloBonoMensaje: 'Oi! Passando pra avisar que teve gente ganhando bem essa semana.',
            repasado: 'Contactado',
            repasoHoy: 'hoje',
            repasoVolvio: 'voltou',
            confirmarEnvio: 'Você mandou mensagem para',
            confirmarSi: 'Sim',
            confirmarNo: 'Ainda não',
            prioridadHoy: 'Para falar hoje',
            marcadorRepasados: 'Contactados',
            marcadorVolvieron: 'voltaram',
        },
        ranking: {
            titulo: 'Ranking',
            descripcion: 'Dispute os 6 trof\u00e9us com os promotores do seu operador. Em qual voc\u00ea reina?',
            posicion: 'Sua posi\u00e7\u00e3o',
            de: 'de',
            afiliados: 'afiliados',
            tu: 'Voc\u00ea',
            sinDatos: 'Ainda n\u00e3o h\u00e1 ranking neste per\u00edodo.',
            trofeo: { general: 'Geral', aquisicao: 'Aquisi\u00e7\u00e3o', valor: 'Valor', qualidade: 'Qualidade', peixe: 'Peixe gordo', retencao: 'Reten\u00e7\u00e3o', conversao: 'Convers\u00e3o' },
            verbo: { general: '\u00e9 o campe\u00e3o geral', aquisicao: 'reina na aquisi\u00e7\u00e3o', valor: 'domina o valor', qualidade: 'lidera em qualidade', peixe: 'fisga os peixes gordos', retencao: 'segura a galera', conversao: 'conquista a convers\u00e3o' },
            vocereina: 'Voc\u00ea REINA aqui',
            vocedomina: 'Voc\u00ea DOMINA',
            nopodio: 'No p\u00f3dio!',
            voce: 'Voc\u00ea',
            destronar: 'falta pouco para destronar o #{n}!',
        },
        campanas: {
            titulo: 'Minhas Campanhas',
            descripcion: 'Veja qual an\u00fancio converte melhor para investir onde rende.',
            campana: 'Campanha',
            fuente: 'Origem',
            captados: 'Captados',
            recargaron: 'Recarregaram',
            jugaron: 'Jogaram',
            sinDatos: 'Ainda n\u00e3o h\u00e1 dados de campanhas neste per\u00edodo.',
        },
        tags: {
            titulo: 'Minhas Tags',
            descripcion: 'Cole seu Pixel da Meta ou GTM para medir e otimizar seus an\u00fancios. Opcional.',
            metaPixel: 'Pixel da Meta (Facebook/Instagram)',
            metaPlaceholder: 'Ex: 1234567890123456',
            gtm: 'Google Tag Manager',
            gtmPlaceholder: 'Ex: GTM-XXXXXXX',
            guardar: 'Salvar',
            guardado: 'Salvo!',
            invalidoMeta: 'Pixel da Meta inv\u00e1lido (s\u00f3 n\u00fameros)',
            invalidoGtm: 'GTM inv\u00e1lido (GTM-XXXXXXX)',
            ayuda: 'Se deixar vazio, usa o do seu operador.',
        },
        embudo: {
            titulo: 'Funil',
            captados: 'Captados',
            registrados: 'Cadastrados',
            conSesion: 'Entraram',
            conRecarga: 'Recarregaram',
            conApuesta: 'Jogaram',
        },
        reditos: {
            titulo: 'Meus ganhos',
            produccion: 'Produção',
            jugado: 'Apostado',
            pagado: 'Pago',
            ggr: 'GGR',
            comision: 'Sua comissão',
            delPeriodo: 'do período',
            aReceber: 'A receber',
            jaRecebido: 'Já recebido',
            estaSemana: 'Esta semana',
            tendSube: '🔥 Tá subindo! Compartilhe mais e continue crescendo.',
            tendBaja: '💪 Caiu essa semana. Compartilhe seu link e volte por cima!',
            tendNeutro: '🚀 Compartilhe seu link e faça sua semana crescer.',
            tuEmbudo: 'Seu funil',
            captados: 'Captou',
            registrados: 'Cadastrados',
            jugando: 'Jogando',
            base: 'Base',
            porcentaje: 'Percentual',
            reditoEstimado: 'Ganho estimado',
            pendiente: 'A receber',
            proximoPago: 'Seu próximo pagamento',
            preview: 'Estimativa informativa. O valor final é confirmado pela central no acerto.',
        },
        notif: {
            titulo: 'Não perca quando receber',
            descripcion: 'Ative os avisos e saiba na hora de cada pagamento.',
            boton: 'Ativar avisos',
            activando: 'Ativando…',
            listo: 'Pronto! Vamos te avisar.',
            error: 'Não foi possível ativar. Tente de novo.',
            tituloIOS: 'Adicione o app à sua tela inicial',
            guiaIOS: 'Toque em Compartilhar → "Adicionar à Tela de Início". Depois ative os avisos aqui.',
            tituloBloqueado: 'Avisos bloqueados',
            guiaBloqueado: 'Ative as notificações para este site nas configurações do navegador.',
        },
        canales: {
            titulo: 'Por onde eles entram',
            descripcion: 'Por qual via chegou cada jogador que você captou. Veja o que mais rende.',
            vacio: 'Ainda sem dados. Compartilhe seu link e volte aqui.',
            juegan: 'jogam',
            recargan: 'recarregam',
            waLista: 'WhatsApp contatos',
            waNum: 'WhatsApp número',
            redes: 'Redes',
            telegram: 'Telegram',
            qr: 'QR',
            copy: 'Link copiado',
            directo: 'Direto',
        },
        misPagos: {
            titulo: 'Meus pagamentos',
            vacio: 'Você ainda não recebeu pagamentos. Quando receber, aparecem aqui.',
            error: 'Não foi possível carregar seus pagamentos.',
        },
        fechas: {
            hoy: 'Hoje',
            semana: '7 dias',
            mes: '30 dias',
        },
        instalar: {
            boton: 'Instalar app',
            iosTitulo: 'Adicione o app à sua tela inicial',
            iosPaso: 'Toque em Compartilhar e depois “Adicionar à Tela de Início”.',
            iosPaso1: 'Toque no botão Compartilhar',
            iosPaso2: 'Escolha “Adicionar à Tela de Início”',
            iosPaso3: 'Pronto! Já está na sua tela',
            iosOk: 'Entendi',
        },
    },

    ru: {
        code: 'ru',
        name: 'Русский',
        errorEmail: {
            noSePudoEnviar:
                'Не удалось отправить письмо прямо сейчас. Попробуйте снова через несколько минут; если ошибка повторяется, сообщите оператору.',
        },
        common: {
            cargando: 'Загрузка…',
            reintentar: 'Повторить',
            sinDatos: 'Нет данных за этот период',
            desde: 'С',
            hasta: 'По',
            actualizar: 'Обновить',
        },
        login: {
            titulo: 'Цифровой промоутер',
            subtitulo: 'Войдите в свой аккаунт',
            correo: 'Эл. почта',
            password: 'Пароль',
            entrar: 'Войти',
            entrando: 'Вход…',
            completar: 'Введите почту и пароль',
            error: 'Не удалось войти',
            sesionExpiro: 'Сессия истекла, войдите снова.',
            sinAcceso: 'Откройте ссылку доступа от оператора, чтобы активировать панель на этом устройстве.',
            enlaceRevocado: 'Ваша ссылка доступа больше недействительна. Запросите новую у оператора.',
            olvido: 'Забыли пароль?',
        },
        activacion: {
            tituloBienvenida: 'Добро пожаловать!',
            subtituloCorreo: 'Подтвердите почту, чтобы активировать аккаунт',
            subtituloPassword: 'Создайте пароль, чтобы начать',
            tuCorreo: 'Ваша почта',
            enviarCodigo: 'Отправить код',
            enviando: 'Отправка…',
            codigoEnviado: 'Мы отправили код на',
            codigo: 'Код из 6 цифр',
            reenviar: 'Отправить код снова',
            password: 'Ваш пароль (минимум 8 символов)',
            passwordRepetir: 'Повторите пароль',
            crearCuenta: 'Активировать аккаунт',
            creando: 'Активация…',
            passwordCorta: 'Пароль должен быть не короче 8 символов',
            passwordNoCoincide: 'Пароли не совпадают',
            correoInvalido: 'Введите корректную почту',
            codigoRequerido: 'Введите код из письма',
            listo: 'Аккаунт активирован! Войдите с почтой и паролем.',
            error: 'Не удалось активировать аккаунт',
            cuentaPendiente: 'Ваш аккаунт готов к активации.',
            subtituloSeleccion: 'На этой кассе несколько аккаунтов — укажите вашу почту, чтобы продолжить',
            continuar: 'Продолжить',
        },
        cambiarPass: {
            titulo: 'Сменить пароль',
            subtitulo: 'Текущий пароль и новый, дважды',
            actual: 'Текущий пароль',
            nueva: 'Новый пароль (минимум 8 символов)',
            nuevaRepetir: 'Повторите новый пароль',
            cambiar: 'Сменить пароль',
            cambiando: 'Смена…',
            faltaActual: 'Введите текущий пароль',
            listo: 'Пароль изменён ✓',
            error: 'Не удалось сменить пароль',
        },
        recuperar: {
            titulo: 'Восстановить пароль',
            subtitulo: 'Мы отправили код на почту, привязанную к аккаунту',
            nueva: 'Новый пароль (минимум 8 символов)',
            restablecer: 'Сбросить пароль',
            restableciendo: 'Сброс…',
            volver: 'Назад ко входу',
            listo: 'Пароль сброшен! Войдите с новым.',
            error: 'Не удалось сбросить пароль',
        },
        cambiarCorreo: {
            titulo: 'Сменить почту',
            subtitulo: 'На новую почту придёт код подтверждения',
            actual: 'Текущая почта',
            nuevo: 'Новая почта',
            passwordActual: 'Текущий пароль',
            confirmar: 'Подтвердить смену',
            confirmando: 'Подтверждение…',
            faltaPassword: 'Введите текущий пароль',
            listo: 'Почта обновлена ✓ Это ваш новый логин.',
            error: 'Не удалось сменить почту',
        },
        nav: {
            compartir: 'Поделиться',
            afiliados: 'Игроки',
            reditos: 'Доход',
            embudo: 'Воронка',
            tags: 'Теги',
            campanas: 'Кампании',
            ranking: 'Рейтинг',
            mas: 'Ещё',
            cambiarPass: 'Сменить пароль',
            cambiarCorreo: 'Сменить почту',
        },
        onboarding: {
            titulo: 'Добро пожаловать, промоутер!',
            intro: 'Вот как вы зарабатываете, делясь своей ссылкой:',
            paso1: 'Поделитесь ссылкой в WhatsApp, Facebook или Instagram.',
            paso2: 'Тот, кто перейдёт по вашей ссылке и сыграет, закрепляется за вами.',
            paso3: 'Вы получаете % комиссии с их игры. Центр платит вам.',
            empezar: 'Начать',
        },
        bienvenida: {
            saludo: 'Ты в деле 🔓',
            marca: 'GOLDEN PARCE',
            frase: 'Ты внутри круга, где люди делятся и зарабатывают.',
            instalarTitulo: 'Установи приложение',
            instalarSub: 'Доступ всегда в один тап.',
            instalarBoton: 'Установить Golden Parce',
            instalarRecomendado: 'Рекомендуется',
            seguirWeb: 'Продолжить в браузере',
            iosTitulo: 'Установить на iPhone',
            iosPaso1: 'Нажми «Поделиться»',
            iosPaso2: 'Выбери «На экран Домой»',
            iosPaso3: 'Готово: открой Golden Parce с экрана',
            iosOk: 'Понятно',
            iosOtroTitulo: 'Открой в Safari',
            iosOtroTexto: 'Чтобы установить приложение на iPhone, открой эту ссылку в Safari.',
            inAppTitulo: 'Открой в браузере',
            inAppTexto: 'Нажми меню (⋯) и выбери «Открыть в Chrome» или «Открыть в браузере», чтобы установить приложение.',
            inAppBoton: 'Открыть в браузере',
            codigoTitulo: 'Почти готово! Подключи аккаунт',
            codigoTexto: 'Введи или вставь код доступа (он есть в твоём сообщении WhatsApp).',
            codigoPlaceholder: 'Твой 6-значный код',
            codigoPegar: 'Вставить',
            codigoPegarManual: 'Вставь свой код здесь (удерживай → Вставить).',
            iosCopiaPrimero: 'ВАЖНО: скопируй код, чтобы вставить позже',
            codigoCopiar: 'Копировать',
            codigoCopiado: 'Скопировано',
            iosCopiaManual: 'Зажми номер и нажми Копировать',
            codigoConectar: 'Подключить',
            codigoError: 'Код недействителен. Проверь и попробуй снова.',
            codigoErrorRed: 'Нет связи. Проверь интернет и попробуй снова.',
            codigoConectando: 'Подключение…',
        },
        editor: {
            negrita: 'Жирный',
            cursiva: 'Курсив',
            tachado: 'Зачёркнутый',
            cita: 'Цитата',
            previa: 'Предпросмотр:',
            teclado: 'Спец. клавиатура',
            emojis: 'Эмодзи',
            espacio: 'пробел',
        },
        compartir: {
            heroTitulo: 'Делитесь и зарабатывайте',
            titulo: 'Ваша ссылка',
            descripcion: 'Делитесь. Они играют — вы зарабатываете. 💸',
            waLista: 'WhatsApp: контактам или группам',
            novoNumero: 'Новый номер',
            meusContatos: 'Мои контакты\nили группы',
            waNumPlaceholder: 'Новый номер (с кодом страны)',
            meta: 'Поделиться в Facebook / Instagram',
            redes: 'Соцсети',
            copiar: 'Скопировать ссылку',
            qr: 'Показать QR',
            cerrar: 'Закрыть',
            qrSub: 'Сканируй, чтобы войти 📲',
            incluirMensaje: 'С сообщением',
            incluirImagen: 'С картинкой',
            emBreve: 'Скоро',
            galeria: 'Сообщения',
            galeriaBtn: 'Шаблоны',
            mensajePlaceholder: 'Напишите сообщение…',
            guardarActual: 'Сохранить это сообщение',
            misMensajes: 'Мои сообщения',
            misMensajesVacio: 'Вы ещё не сохранили сообщения. Напишите и нажмите «Сохранить».',
            sugeridos: 'Предложенные',
            copiado: 'Ссылка скопирована!',
            mensajeLabel: 'Сообщение (можно изменить)',
            sinPasarela: 'Ваша ссылка пока не принимает пополнения — попросите оператора включить шлюз.',
            mensajeDefault: 'Играй со мной! 🎰 Регистрируйся здесь:',
            creatividadesLabel: 'Готовые сообщения (нажмите):',
            creatividades: [
                '➤ *Приз уже здесь. Не хватает только тебя.*\nR$ 5 могут стать *R$ 5.000* за один спин ⚡\n👉 *Заходи и крути:*',
                '✦ *Умножь ставку до x500* за секунды\n_Те же деньги, что на кофе_ — другая история 🔥\n👉 *Преврати сейчас:*',
                '🎯 *R$ 5 → R$ 5.000* за один спин\nМеханика прыжка _настоящая, не слепая удача_\n👉 *Забери свой множитель:*',
                '✦ Твои друзья уже играют\n✦ Не хватает только тебя\n_Не дай им рассказать тебе потом._ 👉 *Присоединяйся:*',
                '⚡ *Крути и выигрывай* за секунды\n✦ Маленькая ставка ▸ большой приз ▸ один спин\n👉 *Умножай сейчас:*',
                '🎰 Играй со мной! Регистрируйся и начни выигрывать:',
            ],
        },
        afiliados: {
            titulo: 'Мои игроки',
            jugador: 'Игрок',
            volumen: 'Ставки',
            ggr: 'GGR',
            totalAfiliados: 'Игроки',
            estado: 'Статус',
            activo: 'Активен',
            dormido: 'Неактивен',
            sinJugar: 'Ещё не играл',
            recargado: 'Пополнил',
            filtroTodos: 'Все',
            filtroPorCobrar: 'К выплате',
            filtroFieis: 'Верные',
            filtroInvestiu: 'Пополнил',
            filtroSoloBono: 'Только бонус',
            copiarNumeros: 'Копировать номера',
            numerosCopiados: 'Номера скопированы',
            copiarMensaje: 'Копировать текст',
            mensajeCopiado: 'Текст скопирован',
            guiaPorCobrarQuienes: 'Выиграли приз и не вывели его.',
            guiaPorCobrarTono: 'Тон: сообщить, не продавать.',
            guiaPorCobrarMensaje: 'Привет! У тебя есть выигрыш на счету. Зайди и выведи его.',
            guiaFielQuienes: 'Пополнили и уже вывели: убедились, что выплаты идут.',
            guiaFielTono: 'Тон: дружеский, как к знакомому клиенту.',
            guiaFielMensaje: 'Привет! Сегодня розыгрыш. Говорю тебе первым.',
            guiaInvirtioQuienes: 'Вложили свои деньги и ещё не выводили.',
            guiaInvirtioTono: 'Тон: поддержать, быть на связи.',
            guiaInvirtioMensaje: 'Привет! Как идёт игра? Пиши, если что.',
            guiaSoloBonoQuienes: 'Потратили бонус и не вернулись.',
            guiaSoloBonoTono: 'Тон: пригласить без давления.',
            guiaSoloBonoMensaje: 'Привет! На этой неделе люди хорошо выигрывали.',
            repasado: 'Связались',
            repasoHoy: 'сегодня',
            repasoVolvio: 'вернулся',
            confirmarEnvio: 'Вы написали',
            confirmarSi: 'Да',
            confirmarNo: 'Ещё нет',
            prioridadHoy: 'Связаться сегодня',
            marcadorRepasados: 'Связались',
            marcadorVolvieron: 'вернулись',
        },
        ranking: {
            titulo: 'Рейтинг',
            descripcion: 'Ваше место среди промоутеров вашего оператора. Поднимайтесь, привлекая больше!',
            posicion: 'Ваше место',
            de: 'из',
            afiliados: 'привлечённых',
            tu: 'Вы',
            sinDatos: 'Пока нет рейтинга за этот период.',
            trofeo: { general: 'Общий', aquisicao: 'Привлечение', valor: 'Ценность', qualidade: 'Качество', peixe: 'Крупная рыба', retencao: 'Удержание', conversao: 'Конверсия' },
            verbo: { general: 'абсолютный чемпион', aquisicao: 'лидер привлечения', valor: 'лидер по ценности', qualidade: 'лидер качества', peixe: 'ловит крупную рыбу', retencao: 'удерживает игроков', conversao: 'лидер конверсии' },
            vocereina: 'Вы ЛИДЕР здесь',
            vocedomina: 'Вы ДОМИНИРУЕТЕ',
            nopodio: 'На пьедестале!',
            voce: 'Вы',
            destronar: 'почти обошли #{n}!',
        },
        campanas: {
            titulo: 'Мои кампании',
            descripcion: 'Смотрите, какая реклама конвертирует лучше, чтобы вкладывать с умом.',
            campana: 'Кампания',
            fuente: 'Источник',
            captados: 'Привлечено',
            recargaron: 'Пополнили',
            jugaron: 'Сыграли',
            sinDatos: 'Пока нет данных по кампаниям за этот период.',
        },
        tags: {
            titulo: 'Мои теги',
            descripcion: 'Вставьте Meta Pixel или GTM, чтобы измерять и оптимизировать рекламу. Необязательно.',
            metaPixel: 'Meta Pixel (Facebook/Instagram)',
            metaPlaceholder: 'Напр.: 1234567890123456',
            gtm: 'Google Tag Manager',
            gtmPlaceholder: 'Напр.: GTM-XXXXXXX',
            guardar: 'Сохранить',
            guardado: 'Сохранено!',
            invalidoMeta: 'Неверный Meta Pixel (только цифры)',
            invalidoGtm: 'Неверный GTM (GTM-XXXXXXX)',
            ayuda: 'Если оставить пустым, используется тег оператора.',
        },
        embudo: {
            titulo: 'Воронка',
            captados: 'Привлечено',
            registrados: 'Зарегистр.',
            conSesion: 'Вошли',
            conRecarga: 'Пополнили',
            conApuesta: 'Сыграли',
        },
        reditos: {
            titulo: 'Мой доход',
            produccion: 'Оборот',
            jugado: 'Ставки',
            pagado: 'Выплачено',
            ggr: 'GGR',
            comision: 'Ваша комиссия',
            delPeriodo: 'за период',
            aReceber: 'К получению',
            jaRecebido: 'Получено',
            estaSemana: 'На этой неделе',
            tendSube: '🔥 Растёшь! Делись больше и продолжай.',
            tendBaja: '💪 Спад на этой неделе. Поделись ссылкой и наверстай!',
            tendNeutro: '🚀 Поделись ссылкой и расти за неделю.',
            tuEmbudo: 'Ваша воронка',
            captados: 'Привлечено',
            registrados: 'Зарегистр.',
            jugando: 'Играют',
            base: 'База',
            porcentaje: 'Процент',
            reditoEstimado: 'Расчётный доход',
            pendiente: 'К выплате',
            proximoPago: 'Ваша следующая выплата',
            preview: 'Информационная оценка. Итоговую сумму подтверждает центр при расчёте.',
        },
        notif: {
            titulo: 'Не пропустите выплату',
            descripcion: 'Включите уведомления и узнавайте о каждом платеже сразу.',
            boton: 'Включить уведомления',
            activando: 'Включаем…',
            listo: 'Готово! Мы сообщим вам.',
            error: 'Не удалось включить. Попробуйте снова.',
            tituloIOS: 'Добавьте приложение на главный экран',
            guiaIOS: 'Нажмите «Поделиться» → «На экран Домой». Затем включите уведомления здесь.',
            tituloBloqueado: 'Уведомления заблокированы',
            guiaBloqueado: 'Включите уведомления для этого сайта в настройках браузера.',
        },
        canales: {
            titulo: 'Откуда они приходят',
            descripcion: 'Через какой канал пришёл каждый игрок. Смотрите, что работает лучше.',
            vacio: 'Пока нет данных. Поделитесь ссылкой и вернитесь.',
            juegan: 'играют',
            recargan: 'пополняют',
            waLista: 'WhatsApp контакты',
            waNum: 'WhatsApp номер',
            redes: 'Соцсети',
            telegram: 'Telegram',
            qr: 'QR',
            copy: 'Ссылка',
            directo: 'Прямой',
        },
        misPagos: {
            titulo: 'Мои выплаты',
            vacio: 'Выплат пока нет. Когда получите, появятся здесь.',
            error: 'Не удалось загрузить выплаты.',
        },
        fechas: {
            hoy: 'Сегодня',
            semana: '7 дней',
            mes: '30 дней',
        },
        instalar: {
            boton: 'Установить приложение',
            iosTitulo: 'Добавьте приложение на главный экран',
            iosPaso: 'Нажмите «Поделиться», затем «На экран Домой».',
            iosPaso1: 'Нажмите кнопку «Поделиться»',
            iosPaso2: 'Выберите «На экран Домой»',
            iosPaso3: 'Готово! Теперь она на экране',
            iosOk: 'Понятно',
        },
    },
};

// Idioma por defecto del brand (Brasil=pt, Perú=es). Sale de VITE_DEFAULT_LOCALE
// (infra/brands → negocio.locale, ej. 'pt-BR' / 'es-PE'), normalizado a 2 letras.
// Antes estaba hardcodeado a 'es' → un iPhone en inglés viendo Roleta Nacional (BR)
// mostraba español en vez de portugués. Si el idioma no está soportado, cae a 'es'.
const IDIOMAS_SOPORTADOS = ['es', 'en', 'pt', 'ru'];
function _idiomaDefaultDelBrand() {
    const raw = (import.meta.env.VITE_DEFAULT_LOCALE || 'es').slice(0, 2).toLowerCase();
    return IDIOMAS_SOPORTADOS.includes(raw) ? raw : 'es';
}

export class PromoterLanguageManager extends LanguageManagerBase {
    constructor() {
        super('promoter-lang', _idiomaDefaultDelBrand(), LANGUAGES);
    }
}

export var i18n = new PromoterLanguageManager();

/**
 * Helper de traducción. @param {string} path dot-notation. @param {Object} params
 */
export function t(path, params) {
    return i18n.t(path, params || {});
}

export default i18n;
